import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Mock credentials for testing
  const mockExistingEmails = ['admin@vibecoding.de', 'test@example.com', 'user@demo.de'];

  const validatePassword = (password) => {
    const errors = [];
    if (password?.length < 8) {
      errors?.push('Mindestens 8 Zeichen erforderlich');
    }
    if (!/[A-Z]/?.test(password)) {
      errors?.push('Mindestens ein Großbuchstabe erforderlich');
    }
    if (!/[a-z]/?.test(password)) {
      errors?.push('Mindestens ein Kleinbuchstabe erforderlich');
    }
    if (!/\d/?.test(password)) {
      errors?.push('Mindestens eine Zahl erforderlich');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/?.test(password)) {
      errors?.push('Mindestens ein Sonderzeichen erforderlich');
    }
    return errors;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) {
      return 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }
    if (mockExistingEmails?.includes(email?.toLowerCase())) {
      return 'Diese E-Mail-Adresse ist bereits registriert';
    }
    return null;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear errors when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }

    // Real-time validation
    if (field === 'email' && value) {
      const emailError = validateEmail(value);
      if (emailError) {
        setErrors(prev => ({ ...prev, email: emailError }));
      }
    }

    if (field === 'password' && value) {
      const passwordErrors = validatePassword(value);
      if (passwordErrors?.length > 0) {
        setErrors(prev => ({ ...prev, password: passwordErrors?.join(', ') }));
      }
    }

    if (field === 'confirmPassword' && value) {
      if (value !== formData?.password) {
        setErrors(prev => ({ ...prev, confirmPassword: 'Passwörter stimmen nicht überein' }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData?.email) {
      newErrors.email = 'E-Mail-Adresse ist erforderlich';
    } else {
      const emailError = validateEmail(formData?.email);
      if (emailError) {
        newErrors.email = emailError;
      }
    }

    // Password validation
    if (!formData?.password) {
      newErrors.password = 'Passwort ist erforderlich';
    } else {
      const passwordErrors = validatePassword(formData?.password);
      if (passwordErrors?.length > 0) {
        newErrors.password = passwordErrors?.join(', ');
      }
    }

    // Confirm password validation
    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwort-Bestätigung ist erforderlich';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwörter stimmen nicht überein';
    }

    // Terms agreement validation
    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'Sie müssen den Nutzungsbedingungen zustimmen';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock successful registration
      console.log('Registration successful:', formData?.email);
      
      // Navigate to email verification or dashboard
      navigate('/ai-agents-dashboard');
    } catch (error) {
      setErrors({ submit: 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.' });
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    const errors = validatePassword(password);
    if (!password) return { strength: 0, label: '', color: '' };
    
    const strength = Math.max(0, 5 - errors?.length);
    const strengthLabels = ['Sehr schwach', 'Schwach', 'Mittel', 'Stark', 'Sehr stark'];
    const strengthColors = ['bg-error', 'bg-warning', 'bg-warning', 'bg-success', 'bg-success'];
    
    return {
      strength: (strength / 5) * 100,
      label: strengthLabels?.[strength - 1] || 'Sehr schwach',
      color: strengthColors?.[strength - 1] || 'bg-error'
    };
  };

  const passwordStrength = getPasswordStrength(formData?.password);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Email Field */}
      <div>
        <Input
          label="E-Mail-Adresse"
          type="email"
          placeholder="ihre.email@beispiel.de"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
          className="w-full"
        />
      </div>
      {/* Password Field */}
      <div>
        <div className="relative">
          <Input
            label="Passwort"
            type={showPassword ? 'text' : 'password'}
            placeholder="Sicheres Passwort erstellen"
            value={formData?.password}
            onChange={(e) => handleInputChange('password', e?.target?.value)}
            error={errors?.password}
            required
            className="w-full pr-12"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
        
        {/* Password Strength Indicator */}
        {formData?.password && (
          <div className="mt-2">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">Passwort-Stärke:</span>
              <span className={`font-medium ${passwordStrength?.strength >= 60 ? 'text-success' : passwordStrength?.strength >= 40 ? 'text-warning' : 'text-error'}`}>
                {passwordStrength?.label}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${passwordStrength?.color}`}
                style={{ width: `${passwordStrength?.strength}%` }}
              />
            </div>
          </div>
        )}
      </div>
      {/* Confirm Password Field */}
      <div>
        <div className="relative">
          <Input
            label="Passwort bestätigen"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Passwort erneut eingeben"
            value={formData?.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
            error={errors?.confirmPassword}
            required
            className="w-full pr-12"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
      </div>
      {/* Terms and Privacy Agreement */}
      <div>
        <Checkbox
          label={
            <span className="text-sm">
              Ich stimme den{' '}
              <a href="/terms" className="text-primary hover:underline font-medium">
                Nutzungsbedingungen
              </a>{' '}
              und der{' '}
              <a href="/privacy" className="text-primary hover:underline font-medium">
                Datenschutzerklärung
              </a>{' '}
              zu
            </span>
          }
          checked={formData?.agreeToTerms}
          onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
          error={errors?.agreeToTerms}
          required
        />
      </div>
      {/* Submit Error */}
      {errors?.submit && (
        <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
          <p className="text-error text-sm">{errors?.submit}</p>
        </div>
      )}
      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={isLoading}
        className="gradient-primary"
      >
        {isLoading ? 'Konto wird erstellt...' : 'Konto erstellen'}
      </Button>
    </form>
  );
};

export default RegistrationForm;